import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Download, ExternalLink, BadgeCheck, Landmark, Phone, FileText } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const CERTS = [
  {
    id: "project-management",
    title: "Project Management & Development Services",
    scope: "Local Owned",
    category: "Business Licenses",
    issuer: "Rungwe District Council",
    number: "BL01510582025-2600001444",
    issued: "12 March 2026",
    validUntil: "11 March 2027",
    thumb: "/certs/project-management-thumb.jpg",
    pdf: "/certs/project-management-license.pdf",
  },
  {
    id: "electronic-money-transfer",
    title: "Electronic Money Transfer (Uwakala)",
    scope: "Financial Services",
    category: "Financial Licenses",
    issuer: "Rungwe District Council",
    number: "BL01510582025-2600001426",
    issued: "10 March 2026",
    validUntil: "9 March 2027",
    thumb: "/certs/electronic-money-thumb.jpg",
    pdf: "/certs/electronic-money-transfer-license.pdf",
  },
  {
    id: "certificate-of-incorporation",
    title: "Certificate of Incorporation",
    scope: "Company Registration",
    category: "Corporate Registration",
    issuer: "Registrar of Companies, Tanzania",
    number: "111329",
    issued: "17 September 2014",
    validUntil: "Perpetual",
    thumb: "/certs/certificate-of-incorporation-thumb.jpg",
    pdf: "/certs/certificate-of-incorporation.pdf",
  },
  {
    id: "certificate-of-registration",
    title: "Certificate of Registration (TIN)",
    scope: "Taxpayer Registration",
    category: "Corporate Registration",
    issuer: "Tanzania Revenue Authority (TRA)",
    number: "138-547-183",
    issued: "08 January 2019",
    validUntil: "Perpetual",
    thumb: "/certs/certificate-of-registration-thumb.jpg",
    pdf: "/certs/certificate-of-registration.pdf",
  },
];

const CATEGORIES = ["All Certifications", "Corporate Registration", "Business Licenses", "Financial Licenses"];

export default function Certs() {
  const navigate = useNavigate();
  const [active, setActive] = useState("All Certifications");

  const filtered = active === "All Certifications" ? CERTS : CERTS.filter((c) => c.category === active);
  const bodies = [...new Set(CERTS.map((c) => c.issuer))].length;

  // Open the PDF inline in a new tab for viewing (bypasses servers that force-download via octet-stream).
  const openPdf = async (url) => {
    const win = window.open("", "_blank");
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      if (win) win.location = objUrl;
      else window.open(objUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    } catch (e) {
      if (win) win.location = url;
    }
  };

  return (
    <div className="bg-brand-grey min-h-screen">
      <SEO
        title="Certifications & Licenses | Tanz-Bridge International LTD"
        description="View and download Tanz-Bridge International LTD's official certifications, business licenses and government accreditations — all verified and up to date."
        path="/certs"
      />
      <Navbar onOrder={() => navigate("/#services")} />

      {/* HERO */}
      <section className="relative pt-32 pb-16 overflow-hidden grain bg-brand-dark">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <motion.p initial="hidden" animate="show" custom={0} variants={fadeUp}
            className="text-xs tracking-[0.3em] uppercase text-brand-yellow mb-4 flex items-center gap-2">
            <ShieldCheck size={16} /> Official Documents
          </motion.p>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-white leading-tight max-w-4xl">
            Official Certifications <span className="text-brand-yellow">&</span> Accreditations
          </motion.h1>
          <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-5 text-base font-medium text-gray-300 max-w-2xl leading-relaxed">
            View and download our official certifications and permits. All documents are verified and
            up-to-date with the relevant regulatory authorities.
          </motion.p>

          {/* Stats */}
          <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp}
            className="mt-10 flex flex-wrap gap-4">
            <div className="rounded-lg bg-white/5 border border-white/10 px-6 py-4 flex items-center gap-4" data-testid="certs-stat-active">
              <BadgeCheck className="text-brand-yellow" size={28} />
              <div>
                <p className="text-2xl font-heading font-extrabold text-white leading-none">{CERTS.length}</p>
                <p className="text-xs text-gray-400 mt-1">Active Certificates</p>
              </div>
            </div>
            <div className="rounded-lg bg-white/5 border border-white/10 px-6 py-4 flex items-center gap-4" data-testid="certs-stat-bodies">
              <Landmark className="text-brand-blue" size={28} />
              <div>
                <p className="text-2xl font-heading font-extrabold text-white leading-none">{bodies}</p>
                <p className="text-xs text-gray-400 mt-1">Certifying Authority</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FILTER + GRID */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex flex-wrap gap-3 mb-10">
          {CATEGORIES.map((cat) => {
            const count = cat === "All Certifications" ? CERTS.length : CERTS.filter((c) => c.category === cat).length;
            return (
              <button
                key={cat}
                data-testid={`certs-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(cat)}
                className={`text-sm font-semibold px-5 py-2.5 rounded-sm border transition-all duration-200 ${
                  active === cat
                    ? "bg-brand-blue border-brand-blue text-white"
                    : "bg-white border-gray-200 text-brand-dark hover:border-brand-blue/50"
                }`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {filtered.map((c, i) => (
            <motion.div
              key={c.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={fadeUp}
              data-testid={`cert-card-${c.id}`}
              className="rounded-lg bg-brand-card border border-gray-200 shadow-sm overflow-hidden flex flex-col"
            >
              <div className="relative bg-brand-dark/5 border-b border-gray-200">
                <img src={c.thumb} alt={c.title} className="w-full h-72 object-cover object-top" />
                <span className="absolute top-4 left-4 bg-brand-yellow text-brand-dark text-xs font-semibold px-3 py-1 rounded-sm flex items-center gap-1">
                  <BadgeCheck size={13} /> Verified
                </span>
              </div>

              <div className="p-6 flex flex-col flex-1">
                <p className="text-xs tracking-[0.2em] uppercase text-brand-blue font-semibold mb-2 flex items-center gap-2">
                  <FileText size={14} /> Government Authorization
                </p>
                <h3 className="text-xl font-heading font-extrabold text-brand-dark leading-snug">{c.title}</h3>
                <p className="text-sm font-medium text-gray-500 mt-1">{c.scope}</p>

                <dl className="mt-5 space-y-2 text-sm border-t border-gray-100 pt-4">
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Issuing Authority</dt>
                    <dd className="text-brand-dark font-medium text-right">{c.issuer}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">License No.</dt>
                    <dd className="text-brand-dark font-medium text-right break-all">{c.number}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Issued</dt>
                    <dd className="text-brand-dark font-medium text-right">{c.issued}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Valid Until</dt>
                    <dd className="text-brand-dark font-medium text-right">{c.validUntil}</dd>
                  </div>
                </dl>

                <div className="mt-6 flex gap-3 pt-2">
                  <Button asChild data-testid={`cert-download-${c.id}`}
                    className="flex-1 bg-brand-blue hover:bg-brand-bluehover text-white rounded-sm">
                    <a href={c.pdf} download>
                      <Download size={16} className="mr-2" /> Download
                    </a>
                  </Button>
                  <Button variant="outline" data-testid={`cert-view-${c.id}`}
                    onClick={() => openPdf(c.pdf)}
                    className="flex-1 border-gray-300 text-brand-dark hover:border-brand-blue rounded-sm">
                    <ExternalLink size={16} className="mr-2" /> Open
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VERIFICATION CTA */}
      <section className="bg-brand-dark">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-extrabold text-white">Need Verification?</h2>
          <p className="mt-3 text-base font-medium text-gray-300 max-w-xl mx-auto">
            Contact us for certificate verification or any additional documentation requirements.
          </p>
          <Button asChild data-testid="certs-verify-btn"
            className="mt-7 bg-brand-yellow hover:bg-brand-yellowhover text-brand-dark font-semibold rounded-sm px-8 py-6">
            <a href="tel:+255712196560"><Phone size={18} className="mr-2" /> Call for Verification</a>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
